
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Phone, MessageCircle, Navigation, MapPin, ShieldCheck, Clock, Shield, User, ShieldAlert, Navigation2, LocateFixed, ArrowRight } from 'lucide-react';
import { JobRequest, UserRole } from '../types';
import { ReportModal } from './ReportModal';
import { listenToCustomerLocation, listenToWorkerLocation, haversineKm } from '../services/firestoreService';

declare var google: any;

interface LiveTrackingScreenProps {
  job: JobRequest;
  onBack: () => void;
  onChat?: () => void;
  onCall?: () => void;
  perspective?: 'customer' | 'worker';
}

const MAP_STYLES = [
  { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#1e293b" }] },
  { "featureType": "landscape", "elementType": "geometry.fill", "stylers": [{ "color": "#f8fafc" }] },
  { "featureType": "poi", "stylers": [{ "visibility": "off" }] },
  { "featureType": "road", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }] },
  { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#e2e8f0" }] },
  { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#e2e8f0" }] }
];

const buildMarkerContent = (avatarUrl: string, name: string, isSelf: boolean) => {
  const container = document.createElement('div');
  container.className = 'marker-container';
  container.style.position = 'relative';

  const img = document.createElement('img');
  img.src = avatarUrl || 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
  img.style.width = '48px';
  img.style.height = '48px';
  img.style.borderRadius = '50%';
  img.style.border = isSelf ? '3px solid #3b82f6' : '3px solid #10b981';
  img.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
  img.style.objectFit = 'cover';

  const pointer = document.createElement('div');
  pointer.style.position = 'absolute';
  pointer.style.bottom = '-8px';
  pointer.style.left = '50%';
  pointer.style.transform = 'translateX(-50%)';
  pointer.style.width = '0';
  pointer.style.height = '0';
  pointer.style.borderLeft = '8px solid transparent';
  pointer.style.borderRight = '8px solid transparent';
  pointer.style.borderTop = isSelf ? '8px solid #3b82f6' : '8px solid #10b981';

  container.appendChild(img);
  container.appendChild(pointer);
  return container;
};

export const LiveTrackingScreen: React.FC<LiveTrackingScreenProps> = ({
  job,
  onBack,
  onChat,
  onCall,
  perspective = 'customer'
}) => {
  const [eta, setEta] = useState(15);
  const [progress, setProgress] = useState(0);
  const [distanceRemaining, setDistanceRemaining] = useState(2.5);
  const [guidanceText, setGuidanceText] = useState('Heading towards pickup point');
  const [isReporting, setIsReporting] = useState(false);
  const [isSheetExpanded, setIsSheetExpanded] = useState(true);
  const [directionsSteps, setDirectionsSteps] = useState<any[]>([]); // Store turn-by-turn directions
  const [showDirections, setShowDirections] = useState(false); // Toggle directions panel
  const mapRef = useRef<HTMLDivElement>(null);

  const mapInstance = useRef<any>(null);
  const workerMarker = useRef<any>(null);
  const userMarker = useRef<any>(null);
  const routePolyline = useRef<any>(null);

  // Directions API Refs
  const directionsService = useRef<any>(null);
  const directionsRenderer = useRef<any>(null);

  const isWorkerPerspective = perspective === 'worker';
  const targetName = isWorkerPerspective ? (job.customerName || 'Customer') : (job.workerName || 'Worker');
  const targetAvatar = isWorkerPerspective ? (job.customerAvatar || '') : (job.workerAvatar || '');

  const [customerPos, setCustomerPos] = useState({ lat: 0, lng: 0 }); // Initialize 0 to prevent Delhi
  const [workerPos, setWorkerPos] = useState({ lat: 0, lng: 0 });

  const customerPosRef = useRef({ lat: 0, lng: 0 });
  const workerPosRef = useRef({ lat: 0, lng: 0 });

  const hasInitializedMap = useRef(false);

  // Safe Job Coords Parsing
  useEffect(() => {
    if (job && job.coordinates && typeof job.coordinates === 'object') {
      const co: any = job.coordinates;
      if (typeof co.x === 'number' && typeof co.y === 'number') {
        // ✅ FIX: PostJobModal saves x as Lat and y as Lng
        // Initialize Customer Location
        const initPos = { lat: co.x, lng: co.y };
        setCustomerPos(initPos);
        customerPosRef.current = initPos;
      }
    } else if (job.workAddress && !job.coordinates) {
      // Fallback logic could go here if address exists but coords don't
    }

    // Initialize Worker Location if available
    if (job.workerId && !isWorkerPerspective) {
      // We let the subscription handle this mostly, but good to init if known
    }

  }, [job]);

  const userLoc = customerPos;
  const workerStartLoc = workerPos;

  // Fit Bounds Logic
  const fitBounds = () => {
    if (mapInstance.current && typeof google !== 'undefined') {
      const bounds = new google.maps.LatLngBounds();
      const w = workerPosRef.current;
      const c = customerPosRef.current;
      if (w && w.lat) bounds.extend(new google.maps.LatLng(w.lat, w.lng));
      if (c && c.lat) bounds.extend(new google.maps.LatLng(c.lat, c.lng));

      mapInstance.current.fitBounds(bounds, {
        top: 150,
        bottom: isSheetExpanded ? 250 : 80,
        left: 50,
        right: 50
      });

      // Force North-Up Re-center
      mapInstance.current.setHeading(0);
      mapInstance.current.setTilt(0);
    }
  };

  // Navigation Logic
  const handleNavigate = () => {
    const dest = isWorkerPerspective ? customerPosRef.current : workerPosRef.current;
    if (dest && dest.lat) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}`;
      window.open(url, '_blank');
    }
  };

  // Helper: Update Markers Position
  const updateMarkers = () => {
    if (!mapInstance.current || !google.maps) return;

    if (workerMarker.current && workerPosRef.current.lat) {
      workerMarker.current.position = new google.maps.LatLng(workerPosRef.current.lat, workerPosRef.current.lng);
    }
    if (userMarker.current && customerPosRef.current.lat) {
      userMarker.current.position = new google.maps.LatLng(customerPosRef.current.lat, customerPosRef.current.lng);
    }
  };

  // Map Initialization
  useEffect(() => {
    const loadMap = () => {
      try {
        if (mapRef.current && !mapInstance.current && typeof google !== 'undefined' && google.maps && google.maps.marker) {
          // console.log("🗺️ Initializing Map...");

          const currentUserLoc = customerPosRef.current;
          const currentWorkerLoc = workerPosRef.current;
          // SAFETY: Verify Center is Valid
          const center = (currentUserLoc && typeof currentUserLoc.lat === 'number' && currentUserLoc.lat !== 0)
            ? currentUserLoc
            : { lat: 28.6420, lng: 77.2250 }; // Only use Delhi for initial Center if absolutely no data

          const mapOptions = {
            center: center,
            zoom: 14,
            disableDefaultUI: true,
            styles: MAP_STYLES,
            zoomControl: false,
            backgroundColor: '#f8fafc',
            mapId: "DEMO_MAP_ID"
          };

          mapInstance.current = new google.maps.Map(mapRef.current, mapOptions);
          mapInstance.current.addListener('click', () => setIsSheetExpanded(false));

          // Create Target Marker
          const targetContent = buildMarkerContent(targetAvatar, targetName, false);

          let targetPosition = isWorkerPerspective ? center : currentWorkerLoc;
          // Note: If I am worker, Center = Customer (Target). Use that.
          // If I am customer, Center = Me. Target = Worker (currentWorkerLoc).

          try {
            // Try Advanced Marker
            if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
              const markerRef = isWorkerPerspective ? userMarker : workerMarker;
              markerRef.current = new google.maps.marker.AdvancedMarkerElement({
                map: mapInstance.current,
                position: targetPosition,
                title: targetName,
                content: targetContent
              });
            } else {
              throw new Error("AdvancedMarkerElement not available");
            }
          } catch (e) {
            console.warn("⚠️ AdvancedMarkerElement failed, using Legacy Marker", e);
            // Fallback to Legacy Marker
            const markerRef = isWorkerPerspective ? userMarker : workerMarker;
            markerRef.current = new google.maps.Marker({
              map: mapInstance.current,
              position: targetPosition,
              title: targetName,
              // scaling for legacy icon if needed, but standard pin is better than nothing
              icon: {
                url: targetAvatar || 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
                scaledSize: new google.maps.Size(40, 40)
              }
            });
          }

          // Initialize Directions (Internal Routing)
          if (!directionsRenderer.current) {
            directionsRenderer.current = new google.maps.DirectionsRenderer({
              map: mapInstance.current,
              suppressMarkers: true, // We have our own custom markers
              polylineOptions: {
                strokeColor: '#3b82f6',
                strokeOpacity: 0.8,
                strokeWeight: 6
              }
            });
          }
          if (!directionsService.current) {
            directionsService.current = new google.maps.DirectionsService();
          }

          hasInitializedMap.current = true;
          // Trigger first route calc
          updateRoute();
        }
      } catch (e) {
        console.error("❌ Map Init Error:", e);
      }
    };

    if (typeof google === 'undefined') {
      const interval = setInterval(() => {
        if (typeof google !== 'undefined') {
          clearInterval(interval);
          loadMap();
        }
      }, 500);
      return () => clearInterval(interval);
    } else {
      loadMap();
    }
  }, []); // Run once on mount

  const updateRoute = () => {
    if (typeof google === 'undefined' || !directionsService.current || !directionsRenderer.current) return;

    const w = workerPosRef.current;
    const c = customerPosRef.current;

    // Strict Check: If using defaults (Delhi), DO NOT draw route
    if (w.lat === 28.6420 && w.lng === 77.2250) return;
    if (c.lat === 28.6420 && c.lng === 77.2250) return;

    if (!w.lat || !w.lng || !c.lat || !c.lng) return;

    // Use REAL road routing
    const origin = new google.maps.LatLng(w.lat, w.lng);
    const destination = new google.maps.LatLng(c.lat, c.lng);

    directionsService.current.route(
      {
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result: any, status: any) => {
        if (status === google.maps.DirectionsStatus.OK) {
          directionsRenderer.current.setDirections(result);

          // Extract ETA and Distance accurately from Route
          if (result.routes[0] && result.routes[0].legs[0]) {
            const leg = result.routes[0].legs[0];
            const distText = leg.distance.text; // e.g. "2.5 km"
            const durText = leg.duration.text; // e.g. "15 mins"
            const distValue = leg.distance.value; // meters
            const durValue = leg.duration.value; // seconds

            // console.log(`🚗 Route Updated: ${distText}, ${durText}`);

            setDistanceRemaining(parseFloat((distValue / 1000).toFixed(1)));
            setEta(Math.ceil(durValue / 60));
            setGuidanceText(`Arriving in ${durText} (${distText})`);
            setProgress(Math.min(100, Math.max(0, 100 - (distValue / 5000) * 100))); // Rough progress bar hack

            // ✅ NEW: Extract turn-by-turn directions steps
            if (leg.steps && leg.steps.length > 0) {
              setDirectionsSteps(leg.steps);
            }
          }
        } else {
          console.warn("Directions API Failed (using fallback line):", status);
          // Fallback: Simple Straight Line
          if (!routePolyline.current) {
            routePolyline.current = new google.maps.Polyline({
              map: mapInstance.current,
              strokeColor: '#3b82f6',
              strokeOpacity: 0.5,
              strokeWeight: 4,
              geodesic: true
            });
          }
          routePolyline.current.setPath([origin, destination]);
          // Clear directions steps on fallback
          setDirectionsSteps([]);
        }
      }
    );
  };

  // Real-time Tracking Effect
  useEffect(() => {
    if (job.status !== 'ACCEPTED' && job.status !== 'IN_PROGRESS') return;

    let watchId: number;
    let unsubOther: () => void = () => { };

    const startBroadcasting = () => {
      if (!navigator.geolocation) return;

      // Immediate One-off
      navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        const newPos = { lat: latitude, lng: longitude };
        if (isWorkerPerspective) {
          setWorkerPos(newPos);
          workerPosRef.current = newPos;
        } else {
          setCustomerPos(newPos);
          customerPosRef.current = newPos;
        }
        if (isWorkerPerspective && workerMarker.current) workerMarker.current.position = new google.maps.LatLng(latitude, longitude);
        if (!isWorkerPerspective && userMarker.current) userMarker.current.position = new google.maps.LatLng(latitude, longitude);

        updateRoute();

        import('../services/supabaseClient').then(({ supabase }) => {
          // ✅ Write to profiles first (realtime subscriptions read from here)
          supabase.from('profiles').update({
            current_lat: latitude,
            current_lng: longitude,
            last_location_update: new Date().toISOString()
          }).eq('id', isWorkerPerspective ? job.workerId : job.customerId).then(() => { });
          // Also update workers table for workers
          if (isWorkerPerspective && job.workerId) {
            supabase.from('workers').update({
              current_lat: latitude,
              current_lng: longitude,
              last_location_update: new Date().toISOString()
            }).eq('id', job.workerId).then(() => { });
          }
        });
      });

      // ✅ FIX: Cross-Tracking Listeners
      let cleanupWorkerSub: any;
      let cleanupCustomerSub: any;

      // If I am Customer -> Listen to Worker Movement
      if (!isWorkerPerspective && job.workerId) {
        cleanupWorkerSub = listenToWorkerLocation(job.workerId, (coords) => {
          if (coords && coords.lat && coords.lng) {
            setWorkerPos(coords);
            workerPosRef.current = coords;
            if (mapInstance.current) updateMarkers();
            updateRoute();
          }
        });
      }

      // If I am Worker -> Listen to Customer Movement / Updates
      if (isWorkerPerspective && job.customerId) {
        cleanupCustomerSub = listenToCustomerLocation(job.customerId, (coords) => {
          if (coords && coords.lat && coords.lng) {
            setCustomerPos(coords);
            customerPosRef.current = coords;
            if (mapInstance.current) updateMarkers();
            updateRoute();
          }
        });
      }

      // Watcher for Self Location Updates
      watchId = navigator.geolocation.watchPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        const newPos = { lat: latitude, lng: longitude };

        if (isWorkerPerspective) {
          setWorkerPos(newPos);
          workerPosRef.current = newPos;
          // No self marker
        } else {
          setCustomerPos(newPos);
          customerPosRef.current = newPos;
          // No self marker
        }
        updateRoute();

        import('../services/supabaseClient').then(({ supabase }) => {
          // ✅ Always write to profiles (real-time location tracking reads from here)
          supabase.from('profiles').update({
            current_lat: latitude,
            current_lng: longitude,
            last_location_update: new Date().toISOString()
          }).eq('id', isWorkerPerspective ? job.workerId : job.customerId).then(() => { });
          // Also update workers table for workers (listenToWorkerLocation reads workers table)
          if (isWorkerPerspective && job.workerId) {
            supabase.from('workers').update({
              current_lat: latitude,
              current_lng: longitude,
              last_location_update: new Date().toISOString()
            }).eq('id', job.workerId).then(() => { });
          }
        });
      }, (err) => console.error(err), { enableHighAccuracy: true });
    };

    const startListening = () => {
      if (isWorkerPerspective) {
        if (!job.customerId) return;
        unsubOther = listenToCustomerLocation(job.customerId, (coords) => {
          setCustomerPos(coords);
          customerPosRef.current = coords;
          if (userMarker.current) {
            userMarker.current.position = new google.maps.LatLng(coords.lat, coords.lng);
            updateRoute();
          }
        });
      } else {
        if (!job.workerId) return;
        unsubOther = listenToWorkerLocation(job.workerId, (coords) => {
          setWorkerPos(coords);
          workerPosRef.current = coords;
          if (workerMarker.current) {
            workerMarker.current.position = new google.maps.LatLng(coords.lat, coords.lng);
            updateRoute();
            // ✅ Use Haversine for accurate distance (replaces broken Euclidean formula)
            const dist = haversineKm(coords.lat, coords.lng, customerPosRef.current.lat, customerPosRef.current.lng);
            setDistanceRemaining(parseFloat(dist.toFixed(1)));
            if (dist < 0.1) setGuidanceText('Arriving');
            else setGuidanceText(`${dist.toFixed(1)} km away`);
          }
        });
      }
    };

    startBroadcasting();
    // Note: cross-tracking listeners are set up inside startBroadcasting already
    // startListening() is kept as fallback for cases where startBroadcasting skips geo
    startListening();

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
      unsubOther();
    };
  }, [job.status, isWorkerPerspective, job.workerId, job.customerId]);

  return (
    <div className="h-full w-full bg-slate-50 dark:bg-slate-950 relative overflow-hidden flex flex-col font-sans animate-fadeIn">
      {/* Map */}
      <div ref={mapRef} className="absolute inset-0 z-0 bg-[#f8fafc] dark:bg-[#0f172a]"></div>

      {/* Top UI - Minimized */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none p-4 safe-top">
        <div className="flex justify-between items-center mb-4">
          <button onClick={onBack} className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-lg p-2.5 rounded-2xl text-slate-900 dark:text-white active:scale-90 transition-all border border-slate-100 dark:border-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button onClick={() => setIsReporting(true)} className="pointer-events-auto bg-red-600 text-white p-2.5 rounded-2xl shadow-xl active:scale-90 transition-all">
            <ShieldAlert className="w-5 h-5" />
          </button>
        </div>
        {/* Navigation Bar Removed per Request to keep it clean */}
      </div>

      {/* Recenter Button - Keep for Map Control */}
      <div className="absolute top-48 right-4 z-20 pointer-events-none flex flex-col gap-2">
        <button onClick={fitBounds} className="pointer-events-auto bg-white dark:bg-slate-900 p-3 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-800 text-blue-600 dark:text-blue-400 active:scale-90 transition-all">
          <LocateFixed className="w-5 h-5" />
        </button>
        {/* Directions Toggle Button */}
        {directionsSteps.length > 0 && (
          <button
            onClick={() => setShowDirections(!showDirections)}
            className="pointer-events-auto bg-white dark:bg-slate-900 p-3 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-800 text-blue-600 dark:text-blue-400 active:scale-90 transition-all"
          >
            <Navigation2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Turn-by-Turn Directions Panel */}
      {showDirections && directionsSteps.length > 0 && (
        <div className="absolute top-24 left-4 right-4 z-20 pointer-events-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 max-h-[50vh] overflow-hidden flex flex-col animate-slideUp">
          <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <Navigation2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-black text-sm text-slate-900 dark:text-white">Turn-by-Turn Directions</h3>
            </div>
            <button onClick={() => setShowDirections(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-4 space-y-3 scrollbar-hide">
            {directionsSteps.map((step: any, index: number) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xs font-black text-blue-600 dark:text-blue-400">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs font-medium text-slate-700 dark:text-slate-300 leading-relaxed"
                  >
                    {(step.instructions || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}
                  </p>
                  <p className="text-[10px] font-bold text-slate-400 mt-1">
                    {step.distance?.text || 'N/A'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Sheet */}
      <div className={`absolute bottom-0 left-0 right-0 z-30 flex flex-col pointer-events-none transition-transform duration-500 cubic-bezier(0.32, 0.72, 0, 1) ${isSheetExpanded ? 'translate-y-0' : 'translate-y-[calc(100%-140px)]'}`}>
        <div className="pointer-events-auto bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 rounded-t-[2.5rem] shadow-[0_-10px_40px_rgba(0,0,0,0.1)] overflow-hidden pb-safe-bottom">
          <div className="w-full flex justify-center pt-3 pb-2 cursor-pointer active:opacity-70" onClick={() => setIsSheetExpanded(!isSheetExpanded)}>
            <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
          </div>
          <div className="px-5 pb-5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img src={targetAvatar || `https://ui-avatars.com/api/?name=${targetName}`} className="w-12 h-12 rounded-2xl object-cover shadow-sm bg-slate-100 dark:bg-slate-800" alt="" />
                <div className="min-w-0">
                  <h3 className="text-sm font-black text-slate-900 dark:text-white truncate leading-none">{targetName}</h3>
                  <p className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                    {progress < 100 ? (
                      <><span className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"></span> {distanceRemaining} km away</>
                    ) : (
                      <><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span> Arrived</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={onCall} className="p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"><Phone className="w-4 h-4 text-slate-900 dark:text-white" /></button>
                <button onClick={onChat} className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-600/20 active:scale-95 transition-all"><MessageCircle className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-1 mt-1">
                  <div className="w-2.5 h-2.5 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
                  <div className="w-0.5 h-8 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="w-2.5 h-2.5 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Current Location</p>
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Live Position</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Destination</p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">{job.workAddress || 'Home Address'}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-1000 ease-linear" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{Math.round(progress)}%</span>
              </div>

              <div className="flex justify-between items-center bg-slate-900 dark:bg-slate-800 rounded-2xl px-5 py-4 shadow-lg text-white">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-green-400" />
                  <p className="text-[9px] font-black opacity-60 uppercase tracking-[0.2em]">{job.status === 'IN_PROGRESS' ? 'COMPLETION CODE' : 'START CODE'}</p>
                </div>
                <span className="text-xl font-black tracking-[0.3em] font-mono">
                  {perspective === 'customer' ? (job.status === 'IN_PROGRESS' ? job.completionOtp : job.otp) : '****'}
                </span>
              </div>
              {perspective === 'worker' && (
                <p className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-wider mt-2">
                  Ask Customer for this code to {job.status === 'IN_PROGRESS' ? 'complete' : 'start'} work
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {isReporting && (
        <ReportModal
          reporterId={perspective === 'customer' ? job.customerId : job.workerId || ''}
          reporterName={perspective === 'customer' ? (job.customerName || 'Customer') : (job.workerName || 'Worker')}
          reportedId={perspective === 'customer' ? (job.workerId || '') : job.customerId}
          reportedName={perspective === 'customer' ? (job.workerName || 'Worker') : (job.customerName || 'Customer')}
          reportedRole={perspective === 'customer' ? UserRole.WORKER : UserRole.CUSTOMER}
          jobId={job.id}
          onClose={() => setIsReporting(false)}
        />
      )}
    </div>
  );
};
